import { ReviewOverviewSnapshot } from "@/services/bedrock";
import { format, endOfWeek, parse } from "date-fns";
import { useLocation } from "react-router-dom";

/** Max bars in the review-volume hero sparkline. Odd so the current period can sit in the center. */
const MAX_VOLUME_BARS = 7;

export type SnapshotOption = ReviewOverviewSnapshot & { label: string };
export type SnapshotPeriod = "monthly" | "weekly" | "all_time";

export const SNAPSHOT_QUERY_PARAM = {
  monthly: "month",
  weekly: "week",
} as const;

export const parseSnapshotPeriodDate = (dateString: string): Date => {
  return parse(dateString.slice(0, 10), "yyyy-MM-dd", new Date());
};

export const formatSnapshotLabel = (snapshot: ReviewOverviewSnapshot): string => {
  if (snapshot.periodType === "all_time") return "All time";

  const periodDate = parseSnapshotPeriodDate(snapshot.periodDate);
  if (snapshot.periodType === "monthly") {
    return format(periodDate, "MMM yyyy");
  }

  const end = endOfWeek(periodDate, { weekStartsOn: 1 });
  return `${format(periodDate, "MMM d")} - ${format(end, "MMM d, yyyy")}`;
};

export const positiveSentimentPercent = (snapshot?: ReviewOverviewSnapshot | null): number | null => {
  if (!snapshot) return null;
  const positive = snapshot.sentimentPositiveCount ?? 0;
  const total = positive + (snapshot.sentimentNeutralCount ?? 0) + (snapshot.sentimentNegativeCount ?? 0);
  if (total === 0) return null;
  return (positive / total) * 100;
};

export type OverviewUrlState = {
  period: SnapshotPeriod;
  date: string | null;
  playerId: number | null;
  segmentationDetailId: number | null;
};

export function useOverviewUrlParams(): OverviewUrlState {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const month = searchParams.get("month");
  const week = searchParams.get("week");
  const playerId = searchParams.get("playerId");
  const segmentationDetailId = searchParams.get("segmentationDetailId");

  let period: SnapshotPeriod = "all_time";
  if (week && !month) {
    period = "weekly";
  } else if (month) {
    period = "monthly";
  }

  return {
    period,
    date: month ?? week,
    playerId: playerId != null && playerId !== "" ? Number(playerId) : null,
    segmentationDetailId:
      segmentationDetailId != null && segmentationDetailId !== "" ? Number(segmentationDetailId) : null,
  };
}

export const findSnapshotByDate = (items: SnapshotOption[], date: string): SnapshotOption | undefined => {
  return items.find(item => item.periodDate.slice(0, 10) === date);
};

/** Snapshot reply rate: replied reviews / total reviews in the period. */
export function replyRateFromSnapshot(snapshot?: ReviewOverviewSnapshot | null): number | null {
  if (!snapshot) return null;
  const total = snapshot.totalReviews;
  if (total === 0) return null;
  return (snapshot.totalRepliedCount / total) * 100;
}

/** Metric fields recalculated by a snapshot refresh (excludes ids, timestamps, and AI fields). */
const SNAPSHOT_METRIC_KEYS = [
  "totalReviews",
  "totalStarReviews",
  "avgStarRating",
  "fiveStarCount",
  "fourStarCount",
  "threeStarCount",
  "twoStarCount",
  "oneStarCount",
  "totalNpsResponses",
  "avgNpsScore",
  "npsNetScore",
  "npsPromoterCount",
  "npsPassiveCount",
  "npsDetractorCount",
  "totalTenPointResponses",
  "avgTenPointScore",
  "totalRepliedCount",
  "totalTextOnlyResponses",
  "sentimentPositiveCount",
  "sentimentNeutralCount",
  "sentimentNegativeCount",
  "facebookReviewCount",
  "zillowReviewCount",
  "googleReviewCount",
  "socialCoachReviewCount",
] as const satisfies readonly (keyof ReviewOverviewSnapshot)[];

export function snapshotMetricsUnchanged(before: ReviewOverviewSnapshot, after: ReviewOverviewSnapshot): boolean {
  return SNAPSHOT_METRIC_KEYS.every(key => (before[key] ?? 0) === (after[key] ?? 0));
}

type VolumeSeriesPoint = {
  value: number;
  label: string;
};

export type VolumeSeries = {
  points: VolumeSeriesPoint[];
  /** Index of the selected/current period within `points`, when known. */
  currentIndex: number;
};

function sortChronological(snapshots: ReviewOverviewSnapshot[]): ReviewOverviewSnapshot[] {
  return [...snapshots].sort((a, b) => a.periodDate.localeCompare(b.periodDate));
}

function toPoints(snapshots: ReviewOverviewSnapshot[]): VolumeSeriesPoint[] {
  return snapshots.map(snapshot => ({
    value: snapshot.totalReviews,
    label: formatSnapshotLabel(snapshot),
  }));
}

/** Window of snapshots with `selected` as centered as edges allow. */
function centeredWindow(
  snapshots: ReviewOverviewSnapshot[],
  selected: ReviewOverviewSnapshot,
  maxBars: number
): { window: ReviewOverviewSnapshot[]; currentIndex: number } {
  const sorted = sortChronological(snapshots);
  if (sorted.length === 0) {
    return { window: [selected], currentIndex: 0 };
  }

  const selectedDate = selected.periodDate.slice(0, 10);
  let idx = sorted.findIndex(s => s.id === selected.id);
  if (idx < 0) {
    idx = sorted.findIndex(s => s.periodDate.slice(0, 10) === selectedDate);
  }
  if (idx < 0) {
    // Selected not in list — append and take the trailing window.
    const withSelected = sortChronological([...sorted, selected]);
    const window = withSelected.slice(-maxBars);
    return {
      window,
      currentIndex: Math.max(
        0,
        window.findIndex(s => s.id === selected.id || s.periodDate.slice(0, 10) === selectedDate)
      ),
    };
  }

  const radius = Math.floor((maxBars - 1) / 2);
  let start = idx - radius;
  let end = idx + radius + 1;

  if (start < 0) {
    end = Math.min(sorted.length, end - start);
    start = 0;
  }
  if (end > sorted.length) {
    start = Math.max(0, start - (end - sorted.length));
    end = sorted.length;
  }

  const window = sorted.slice(start, end);
  return { window, currentIndex: idx - start };
}

/**
 * Builds a chronological review-volume series for the Reviews hero sparkline.
 *
 * - Month → monthly snapshot totals, selected month centered when past/future exist
 * - Week → weekly snapshot totals, selected week centered when past/future exist
 * - All time → most recent weekly totals
 */
export function buildVolumeSeries({
  period,
  selected,
  monthlySnapshots,
  weeklySnapshots,
  maxBars = MAX_VOLUME_BARS,
}: {
  period: SnapshotPeriod;
  selected?: ReviewOverviewSnapshot | null;
  monthlySnapshots: ReviewOverviewSnapshot[];
  weeklySnapshots: ReviewOverviewSnapshot[];
  maxBars?: number;
}): VolumeSeries {
  if (period === "all_time") {
    const window = sortChronological(weeklySnapshots).slice(-maxBars);
    return {
      points: toPoints(window),
      currentIndex: Math.max(0, window.length - 1),
    };
  }

  if (!selected) return { points: [], currentIndex: 0 };

  const source = period === "weekly" ? weeklySnapshots : monthlySnapshots;
  const { window, currentIndex } = centeredWindow(source, selected, maxBars);
  return {
    points: toPoints(window),
    currentIndex,
  };
}
